/**
 * 📐 내 표 설정 — 설정 모드 ON/OFF 토글 + 초기화 (segmented 한 덩어리).
 *
 * 외부 사용:
 *   <ExcelModeGroup
 *     enabled
 *     hasPrefs
 *     userName
 *     onChange={fn}
 *     onReset={fn}
 *   />
 *
 *  - 왼쪽 [📐 내 표 설정] : 단순 ON/OFF 토글 (확인 다이얼로그 없음)
 *  - 오른쪽 [↺ 초기화]    : prefs 가 있을 때만 노출. 확인 후 모든 prefs 삭제 + OFF.
 *  - 두 버튼이 같은 테두리로 한 덩어리처럼 보이되, 마우스 호버는 각자 부분만 색 반전.
 *  - 모바일 (md 미만) 에선 그룹 자체 숨김 — 모바일은 PC 에서 설정한 대로 표출.
 */

interface Props {
  enabled: boolean;
  hasPrefs: boolean;
  userName: string;
  onChange: (next: boolean) => void;
  onReset: () => void;
}

export function ExcelModeGroup({
  enabled,
  hasPrefs,
  userName,
  onChange,
  onReset,
}: Props) {
  function handleReset() {
    const ok = window.confirm(
      `${userName} 님이 지정한 컬럼 순서·너비·표시 설정을 모두 기본값으로 되돌리겠습니까?\n\n` +
        `(설정 모드도 함께 꺼집니다)`,
    );
    if (ok) onReset();
  }

  // 한 덩어리 segmented — 인접 border 공유, 모서리 한쪽만 둥글게.
  const baseBtn =
    'inline-flex items-center gap-1 px-2.5 py-1 text-xs border transition';
  const toggleCls = enabled
    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm'
    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50';
  const resetCls =
    'bg-white text-slate-600 border-slate-300 hover:bg-slate-100';

  // 단독일 때(초기화 없음) 와 segmented 일 때 모서리 처리 분리.
  const toggleRounding = hasPrefs ? 'rounded-l border-r-0' : 'rounded';
  const resetRounding = 'rounded-r -ml-px';

  return (
    <div className="hidden md:inline-flex">
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`${baseBtn} ${toggleCls} ${toggleRounding}`}
        title={
          enabled
            ? '설정 모드 끄기 (지정한 컬럼 순서·너비·표시는 그대로 유지됨)'
            : '컬럼 순서·너비·표시 컬럼을 내 맘대로 조정 (PC 전용, 자동 저장)'
        }
      >
        📐 내 표 설정{enabled ? ' 켜짐' : ''}
      </button>
      {hasPrefs && (
        <button
          type="button"
          onClick={handleReset}
          className={`${baseBtn} ${resetCls} ${resetRounding}`}
          title="컬럼 순서·너비·표시 설정을 기본값으로 되돌림"
        >
          ↺ 초기화
        </button>
      )}
    </div>
  );
}
